// 價格計算相關工具函數
import { formatPrice } from './format';

/**
 * 計算團購折扣
 * @param {number} quantity - 購買數量
 * @param {Object} bulkDiscounts - 團購折扣設定 { 1: 0.8, 2: 0.7, ... }
 * @returns {number} - 折扣率 (0.5 表示 5 折)
 */
export const calculateBulkDiscount = (quantity, bulkDiscounts = {}) => {
  if (!bulkDiscounts || Object.keys(bulkDiscounts).length === 0) {
    return 1; // 無折扣
  }
  
  // 找出適用的折扣（取最大的適用數量門檻）
  const sortedThresholds = Object.keys(bulkDiscounts)
    .map(Number)
    .sort((a, b) => b - a);
  
  for (const threshold of sortedThresholds) {
    if (quantity >= threshold) {
      return bulkDiscounts[threshold];
    }
  }
  
  return 1; // 未達任何門檻
};

/**
 * 取得團購折扣資訊
 * @param {number} quantity - 購買數量
 * @param {Object} bulkDiscounts - 團購折扣設定
 * @returns {Object} - 折扣資訊
 */
export const getBulkDiscountInfo = (quantity, bulkDiscounts = {}) => {
  if (!bulkDiscounts || Object.keys(bulkDiscounts).length === 0) {
    return {
      applicable: false,
      threshold: 0,
      rate: 1,
      discountPercentage: 0,
      message: '',
    };
  }
  
  const sortedThresholds = Object.keys(bulkDiscounts)
    .map(Number)
    .sort((a, b) => b - a);
  
  // 檢查當前數量是否已達折扣門檻
  for (const threshold of sortedThresholds) {
    if (quantity >= threshold) {
      const rate = bulkDiscounts[threshold];
      const discountPercentage = Math.round((1 - rate) * 100);
      
      return {
        applicable: true,
        threshold,
        rate,
        discountPercentage,
        message: `${threshold} 件享 ${Math.round(rate * 10)} 折優惠`,
      };
    }
  }
  
  // 提示下一個折扣門檻
  const nextThreshold = sortedThresholds.find(t => t > quantity);
  if (nextThreshold) {
    const nextRate = bulkDiscounts[nextThreshold];
    const remaining = nextThreshold - quantity;
    
    return {
      applicable: false,
      threshold: nextThreshold,
      rate: nextRate,
      discountPercentage: Math.round((1 - nextRate) * 100),
      message: `再買 ${remaining} 件享 ${Math.round(nextRate * 10)} 折`,
      nextThreshold,
      remaining,
    };
  }
  
  return {
    applicable: false,
    threshold: 0,
    rate: 1,
    discountPercentage: 0,
    message: '',
  };
};

/**
 * 重新導出 formatPrice 以保持向後兼容
 */
export { formatPrice } from './format';

/**
 * 計算達到下一個優惠等級可以省下的金額
 * @param {number} currentQuantity - 當前購買數量
 * @param {number} itemPrice - 單價
 * @param {Object} bulkDiscounts - 團購折扣設定
 * @returns {Object} - 下一級優惠資訊
 */
export const getNextTierSavings = (currentQuantity, itemPrice, bulkDiscounts = {}) => {
  if (!bulkDiscounts || Object.keys(bulkDiscounts).length === 0) {
    return null;
  }
  
  const sortedThresholds = Object.keys(bulkDiscounts)
    .map(Number)
    .sort((a, b) => a - b); // 從小到大排序
  
  // 找出當前適用的折扣
  let currentRate = 1;
  for (const threshold of sortedThresholds.slice().reverse()) {
    if (currentQuantity >= threshold) {
      currentRate = bulkDiscounts[threshold];
      break;
    }
  }
  
  // 找出下一個門檻
  const nextThreshold = sortedThresholds.find(t => t > currentQuantity);
  if (!nextThreshold) {
    return null; // 已達最高優惠等級
  }
  
  const nextRate = bulkDiscounts[nextThreshold];
  const needToBuy = nextThreshold - currentQuantity;
  
  // 計算當前總價
  const currentTotal = currentQuantity * itemPrice * currentRate;
  
  // 計算達到下一級的總價
  const nextTierQuantity = nextThreshold;
  const nextTierTotal = nextTierQuantity * itemPrice * nextRate;
  
  // 計算省下的金額（相對於現在再買到下一級的狀況）
  // 如果現在再買 needToBuy 件，使用當前折扣
  const additionalCostAtCurrentRate = needToBuy * itemPrice * currentRate;
  const totalIfBuyMore = currentTotal + additionalCostAtCurrentRate;
  
  // 與達到下一級的總價比較
  const savings = totalIfBuyMore - nextTierTotal;
  
  const savingsAmount = Math.round(savings);
  
  return {
    needToBuy,
    nextThreshold,
    nextRate,
    nextDiscountPercentage: Math.round((1 - nextRate) * 100),
    savings: savingsAmount,
    currentRate,
    message: `再加購 ${needToBuy} 件，升級享 ${Math.round(nextRate * 10)} 折，可再省 NT$${savingsAmount.toLocaleString('zh-TW')}`,
  };
};

/**
 * 計算小計（未套用折扣）
 * @param {Array} items - 購物車商品陣列
 * @returns {number} - 小計金額
 */
export const calculateSubtotal = (items = []) => {
  return items.reduce((total, item) => {
    return total + (item.price * item.quantity);
  }, 0);
};

/**
 * 計算團購折扣後金額
 * @param {number} subtotal - 小計
 * @param {number} quantity - 總數量
 * @param {Object} bulkDiscounts - 團購折扣設定
 * @returns {number} - 折扣後金額
 */
export const calculateDiscountedTotal = (subtotal, quantity, bulkDiscounts = {}) => {
  const discountRate = calculateBulkDiscount(quantity, bulkDiscounts);
  return Math.round(subtotal * discountRate);
};

/**
 * 運費設定常數
 */
export const SHIPPING_CONFIG = {
  FREE_SHIPPING_THRESHOLD: 3000,  // 免運門檻
  BASE_SHIPPING_FEE: 60,           // 基本運費
  COD_FREE_SHIPPING: 3000,         // 貨到付款免運門檻
};

/**
 * 計算運費
 * @param {number} total - 訂單總金額（商品金額，不含運費）
 * @param {string} paymentMethod - 付款方式 (credit_card, linepay, atm, cvs, cod)
 * @param {string} region - 配送地區
 * @returns {number} - 運費
 */
export const calculateShipping = (total, paymentMethod = 'credit_card', region = '本島') => {
  // 🚚 貨到付款 (COD) 運費規則
  if (paymentMethod === 'cod') {
    // 滿 NT$3,000 免運費
    if (total >= SHIPPING_CONFIG.COD_FREE_SHIPPING) {
      return 0;
    }
    // 未滿 NT$3,000 收取 NT$60 運費
    return SHIPPING_CONFIG.BASE_SHIPPING_FEE;
  }
  
  // 💳 線上付款（信用卡、LINE Pay、ATM、超商代碼）
  // 目前全部免運
  return 0;
  
  // 未來可擴充不同地區運費
  // if (region === '離島') return 120;
  // if (total >= SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD) return 0;
  // return SHIPPING_CONFIG.BASE_SHIPPING_FEE;
};

/**
 * 驗證折扣碼
 * @param {string} code - 折扣碼
 * @param {number} quantity - 購買數量
 * @returns {Object|null} - 折扣碼資訊或 null
 */
export const validateDiscountCode = (code, quantity = 1) => {
  const upperCode = code.toUpperCase();
  
  // 折扣碼資料庫（實際應從後端 API 取得）
  const discountCodes = {
    'NMN1000': {
      code: 'NMN1000',
      type: 'fixed',
      amount: 1000,
      description: '首次體驗折扣 - 折抵 $1,000',
      minQuantity: 1,
      oneTimeUse: true,
      active: true,
    },
    'WELCOME500': {
      code: 'WELCOME500',
      type: 'fixed',
      amount: 500,
      description: '新會員歡迎禮 - 折抵 $500',
      minQuantity: 1,
      oneTimeUse: true,
      active: true,
    },
    'VIP10': {
      code: 'VIP10',
      type: 'percentage',
      amount: 10,
      description: 'VIP 會員專屬 - 額外 9 折',
      minQuantity: 1,
      oneTimeUse: false,
      active: true,
    },
  };
  
  const discount = discountCodes[upperCode];
  
  if (!discount) {
    return { valid: false, error: '折扣碼不存在' };
  }
  
  if (!discount.active) {
    return { valid: false, error: '折扣碼已失效' };
  }
  
  if (quantity < discount.minQuantity) {
    return {
      valid: false,
      error: `此折扣碼需購買至少 ${discount.minQuantity} 件商品`,
    };
  }
  
  // 檢查是否已使用過（從 localStorage）
  if (discount.oneTimeUse) {
    const usedCodes = JSON.parse(localStorage.getItem('used_discount_codes') || '[]');
    if (usedCodes.includes(upperCode)) {
      return { valid: false, error: '此折扣碼已使用過' };
    }
  }
  
  return {
    valid: true,
    ...discount,
  };
};

/**
 * 套用折扣碼
 * @param {string} code - 折扣碼
 * @param {number} total - 折扣前金額
 * @param {number} quantity - 購買數量
 * @returns {Object} - 套用結果 { success, amount, newTotal, message }
 */
export const applyDiscountCode = (code, total, quantity = 1) => {
  const validation = validateDiscountCode(code, quantity);
  
  if (!validation.valid) {
    return {
      success: false,
      amount: 0,
      newTotal: total,
      message: validation.error,
    };
  }
  
  let discountAmount = 0;
  
  if (validation.type === 'fixed') {
    // 固定金額折扣
    discountAmount = Math.min(validation.amount, total);
  } else if (validation.type === 'percentage') {
    // 百分比折扣
    discountAmount = Math.round(total * (validation.amount / 100));
  }
  
  const newTotal = Math.max(0, total - discountAmount);
  
  return {
    success: true,
    amount: discountAmount,
    newTotal,
    message: validation.description,
    code: validation.code,
    type: validation.type,
  };
};

/**
 * 標記折扣碼為已使用
 * @param {string} code - 折扣碼
 */
export const markDiscountCodeAsUsed = (code) => {
  const upperCode = code.toUpperCase();
  const usedCodes = JSON.parse(localStorage.getItem('used_discount_codes') || '[]');
  
  if (!usedCodes.includes(upperCode)) {
    usedCodes.push(upperCode);
    localStorage.setItem('used_discount_codes', JSON.stringify(usedCodes));
  }
};

/**
 * 計算最終總金額
 * @param {Array} items - 購物車商品
 * @param {string|null} discountCode - 折扣碼
 * @param {string} region - 配送地區
 * @returns {Object} - 完整價格明細
 */
export const calculateFinalTotal = (items = [], discountCode = null, region = '本島') => {
  // 1. 計算小計
  const subtotal = calculateSubtotal(items);
  
  // 2. 計算總數量
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  
  // 3. 取得團購折扣
  const bulkDiscounts = items[0]?.bulkDiscounts || {};
  const bulkDiscountRate = calculateBulkDiscount(totalQuantity, bulkDiscounts);
  const bulkDiscountInfo = getBulkDiscountInfo(totalQuantity, bulkDiscounts);
  
  // 4. 套用團購折扣
  const afterBulkDiscount = Math.round(subtotal * bulkDiscountRate);
  const bulkDiscountAmount = subtotal - afterBulkDiscount;
  
  // 5. 套用折扣碼
  let discountCodeAmount = 0;
  let discountCodeInfo = null;
  
  if (discountCode) {
    const result = applyDiscountCode(discountCode, afterBulkDiscount, totalQuantity);
    if (result.success) {
      discountCodeAmount = result.amount;
      discountCodeInfo = {
        code: result.code,
        type: result.type,
        amount: result.amount,
        description: result.message,
      };
    }
  }
  
  // 6. 計算運費
  const afterDiscount = afterBulkDiscount - discountCodeAmount;
  const shipping = calculateShipping(afterDiscount, region);
  
  // 7. 最終總金額
  const total = Math.max(0, afterDiscount + shipping);
  
  // 8. 計算總省下金額
  const totalSavings = subtotal - total + shipping;
  
  return {
    subtotal,
    totalQuantity,
    bulkDiscountRate,
    bulkDiscountAmount,
    bulkDiscountInfo,
    discountCodeAmount,
    discountCodeInfo,
    shipping,
    total,
    totalSavings,
  };
};

/**
 * 計算折扣百分比
 * @param {number} originalPrice - 原價
 * @param {number} salePrice - 售價
 * @returns {number} - 折扣百分比
 */
export const calculateDiscountPercentage = (originalPrice, salePrice) => {
  if (originalPrice <= 0 || salePrice >= originalPrice) return 0;
  return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
};

/**
 * 檢查是否符合免運條件
 * @param {number} total - 訂單金額
 * @param {number} threshold - 免運門檻
 * @returns {Object} - 免運資訊
 */
export const checkFreeShipping = (total, threshold = 0) => {
  if (threshold === 0) {
    return {
      eligible: true,
      message: '免運費',
    };
  }
  
  if (total >= threshold) {
    return {
      eligible: true,
      message: `已達免運門檻 NT$${threshold.toLocaleString('zh-TW')}`,
    };
  }
  
  const remaining = threshold - total;
  return {
    eligible: false,
    remaining,
    message: `再購買 NT$${remaining.toLocaleString('zh-TW')} 即可免運`,
  };
};
